import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import PropTypes from 'prop-types';

/**
 * CourtHlsPlayer - Optimized HLS player for court streaming
 * Configured for longer playback and better buffering
 */
function CourtHlsPlayer({ 
  hlsUrl, 
  autoPlay = true,
  onError
}) {
  const videoRef = useRef(null);
  const hlsInstanceRef = useRef(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const cleanupHls = () => {
    if (hlsInstanceRef.current) {
      // Clear buffer monitor
      if (hlsInstanceRef.current.bufferMonitor) {
        clearInterval(hlsInstanceRef.current.bufferMonitor);
      }
      
      hlsInstanceRef.current.destroy();
      hlsInstanceRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.src = '';
      videoRef.current.load();
    }
  };

  const initializeHls = async () => {
    if (!hlsUrl || !videoRef.current) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Clean up any existing instance
      cleanupHls();

      if (Hls.isSupported()) {
        
        const hls = new Hls({
          // Aggressive buffering settings for continuous playback
          maxBufferLength: 600,        // 10 minutes buffer
          maxMaxBufferLength: 1200,    // 20 minutes max buffer
          backBufferLength: 300,       // 5 minutes back buffer
          maxBufferSize: 100 * 1000 * 1000, // 100MB buffer
          maxBufferHole: 0.1,          // Smaller gaps allowed
          
          // Retry settings
          maxLoadingRetry: 15,
          maxRetryDelay: 3000,
          
          // Performance settings

          lowLatencyMode: false,       // Disable low latency for longer playback
          
          // Live settings - more conservative for stability
          liveSyncDurationCount: 5,
          liveMaxLatencyDurationCount: 15,
          liveBackBufferLength: 60,    // Keep 1 minute of back buffer
          
          // Fragment settings
          maxFragLookUpTolerance: 0.1,
          fragLoadingTimeOut: 30000,
          manifestLoadingTimeOut: 15000,
          
          // Buffer management
          highBufferWatchdogPeriod: 2,
          
          // Debug logging
          debug: false  // Disable debug logging for production
        });

        hlsInstanceRef.current = hls;

        // Event handlers
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsLoading(false);
          setError(null);
          
          // Buffer monitoring (optional - can be enabled for debugging)
          // const bufferMonitor = setInterval(() => {
          //   if (videoRef.current && videoRef.current.buffered.length > 0) {
          //     const buffered = videoRef.current.buffered;
          //     const currentTime = videoRef.current.currentTime;
          //     let bufferedAhead = 0;
          //     
          //     for (let i = 0; i < buffered.length; i++) {
          //       if (buffered.start(i) <= currentTime && buffered.end(i) > currentTime) {
          //         bufferedAhead = buffered.end(i) - currentTime;
          //         break;
          //       }
          //     }
          //     
          //   }
          // }, 5000);
          // hls.bufferMonitor = bufferMonitor;
          
          if (autoPlay) {
            videoRef.current.play().catch(e => {
            });
          }
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                const errorMsg = 'Fatal error occurred while loading stream';
                setError(errorMsg);
                onError?.(errorMsg);
                cleanupHls();
                break;
            }
          }
        });

        hls.on(Hls.Events.BUFFER_STALLED, () => {
        });

        // Optional: Enable these for debugging
        // hls.on(Hls.Events.BUFFER_APPENDED, () => {
        // });
        // hls.on(Hls.Events.BUFFER_FLUSHED, () => {
        // });
        // hls.on(Hls.Events.BUFFER_RESET, () => {
        // });
        // hls.on(Hls.Events.FRAG_LOADED, () => {
        // });
        // hls.on(Hls.Events.FRAG_LOADING, () => {
        // });

        // Load the source
        hls.loadSource(hlsUrl);
        hls.attachMedia(videoRef.current);
        
      } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        videoRef.current.src = hlsUrl;
        videoRef.current.addEventListener('loadedmetadata', () => {
          setIsLoading(false);
          setError(null);
          
          if (autoPlay) {
            videoRef.current.play().catch(e => {
            });
          }
        });
        videoRef.current.addEventListener('error', (e) => {
          setError('Stream connection failed');
          onError?.('Stream connection failed');
        });
      } else {
        const errorMsg = 'HLS is not supported in this browser';
        setError(errorMsg);
        onError?.(errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Failed to initialize video player';
      setError(errorMsg);
      onError?.(errorMsg);
    }
  };

  useEffect(() => {
    if (hlsUrl) {
      initializeHls();
    } else {
      cleanupHls();
      setIsLoading(false);
      setError(null);
    }

    return cleanupHls;
  }, [hlsUrl]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          background: '#000'
        }}
        controls
        playsInline
        muted={autoPlay} // Mute for autoplay compliance
      />
      
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          background: 'rgba(0, 0, 0, 0.7)',
          padding: '1rem',
          borderRadius: '4px',
          textAlign: 'center',
          zIndex: 10
        }}>
          Loading stream...
        </div>
      )}
      
      {error && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          background: 'rgba(0, 0, 0, 0.7)',
          padding: '1rem',
          borderRadius: '4px',
          textAlign: 'center',
          zIndex: 10
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

CourtHlsPlayer.propTypes = {
  hlsUrl: PropTypes.string.isRequired,
  autoPlay: PropTypes.bool,
  onError: PropTypes.func
};

export default CourtHlsPlayer;
